import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useResetPassword } from "@/hooks/api/auth";
import { isAxiosError } from "@/lib/api";

const schema = z.object({
  password: z.string().min(10, "At least 10 characters"),
});

type FormValues = z.infer<typeof schema>;

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const reset = useResetPassword();
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const token = searchParams.get("token");

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: "" },
  });

  const onSubmit = async (data: FormValues) => {
    setServerError(null);
    if (!token) {
      setServerError(t("auth.reset_missing_token"));
      return;
    }
    try {
      await reset.mutateAsync({ token, password: data.password });
      setSubmitted(true);
    } catch (err) {
      if (isAxiosError(err)) {
        const detail = (err.response?.data as { detail?: string | string[] } | undefined)
          ?.detail;
        setServerError(
          Array.isArray(detail) ? detail.join(" ") : detail ?? t("auth.reset_failed")
        );
      } else {
        setServerError(t("auth.reset_failed"));
      }
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-page px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl">{t("auth.reset_title")}</CardTitle>
          <CardDescription>{t("auth.reset_subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          {!token && (
            <div
              role="alert"
              className="rounded-md border border-danger/30 bg-danger-subtle p-3 text-sm text-danger"
            >
              {t("auth.reset_missing_token")}
            </div>
          )}

          {submitted ? (
            <div
              role="status"
              className="rounded-md border border-success/30 bg-success-subtle p-3 text-sm text-success"
            >
              {t("auth.reset_success")}
            </div>
          ) : (
            token && (
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-4"
                  noValidate
                >
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("auth.reset_new_password")}</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            autoComplete="new-password"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {serverError && (
                    <div
                      role="alert"
                      className="rounded-md border border-danger/30 bg-danger-subtle p-3 text-sm text-danger"
                    >
                      {serverError}
                    </div>
                  )}

                  <Button type="submit" className="w-full" loading={reset.isPending}>
                    {t("auth.reset_submit")}
                  </Button>
                </form>
              </Form>
            )
          )}

          <div className="mt-6 text-center text-sm text-slate-600">
            <Link to="/login" className="text-primary hover:underline">
              {t("auth.back_to_login")}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
